import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Bug, Lightbulb, Heart, Star,
  CheckCircle, Clock, Archive, Trash2, RefreshCw,
  Mail, User, Calendar, BarChart3, Filter, LogOut
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiEndpoints } from '../../config/api';

interface Feedback {
  id: string;
  name: string;
  email: string;
  type: 'general' | 'bug' | 'feature' | 'love';
  rating: number | null;
  message: string;
  status: 'new' | 'reviewed' | 'resolved' | 'archived';
  created_at: string;
  admin_notes?: string;
}

interface Stats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  averageRating: number;
}

const typeConfig = {
  general: { label: 'General', icon: MessageSquare, color: 'bg-blue-100 text-blue-700' },
  bug: { label: 'Bug Report', icon: Bug, color: 'bg-red-100 text-red-700' },
  feature: { label: 'Feature Request', icon: Lightbulb, color: 'bg-amber-100 text-amber-700' },
  love: { label: 'Love', icon: Heart, color: 'bg-pink-100 text-pink-700' },
};

const statusConfig = {
  new: { label: 'New', icon: Clock, color: 'bg-blue-100 text-blue-700' },
  reviewed: { label: 'Reviewed', icon: CheckCircle, color: 'bg-yellow-100 text-yellow-700' },
  resolved: { label: 'Resolved', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  archived: { label: 'Archived', icon: Archive, color: 'bg-gray-100 text-gray-700' },
};

const DashboardPage = () => {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const { logout } = useAuth();
  const navigate = useNavigate();

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('type', filterType);

      const response = await fetch(`${apiEndpoints.feedback}?${params}`);
      const data = await response.json();
      if (data.success) {
        setFeedback(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${apiEndpoints.feedback}/stats/summary`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [filterStatus, filterType]);

  const updateFeedbackStatus = async (id: string, status: string) => {
    try {
      await fetch(`${apiEndpoints.feedback}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetchFeedback();
      fetchStats();
      if (selectedFeedback?.id === id) {
        setSelectedFeedback({ ...selectedFeedback, status: status as Feedback['status'] });
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  const deleteFeedback = async (id: string) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;
    try {
      await fetch(`${apiEndpoints.feedback}/${id}`, { method: 'DELETE' });
      setSelectedFeedback(null);
      fetchFeedback();
      fetchStats();
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyclara-500 to-pink-500
                          flex items-center justify-center text-white text-xl">
              🌸
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Cyclara Admin</h1>
              <p className="text-sm text-slate-500">Feedback Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchFeedback(); fetchStats(); }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200
                       rounded-lg text-slate-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100
                       rounded-lg text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="w-6 md:w-8 h-6 md:h-8 text-cyclara-500" />
              <span className="text-xl md:text-2xl font-bold text-slate-800">{stats?.total || 0}</span>
            </div>
            <p className="text-slate-600 text-sm md:text-base">Total Feedback</p>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-6 md:w-8 h-6 md:h-8 text-blue-500" />
              <span className="text-xl md:text-2xl font-bold text-slate-800">{stats?.byStatus?.new || 0}</span>
            </div>
            <p className="text-slate-600 text-sm md:text-base">New (Unread)</p>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Bug className="w-6 md:w-8 h-6 md:h-8 text-red-500" />
              <span className="text-xl md:text-2xl font-bold text-slate-800">{stats?.byType?.bug || 0}</span>
            </div>
            <p className="text-slate-600 text-sm md:text-base">Bug Reports</p>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Star className="w-6 md:w-8 h-6 md:h-8 text-yellow-500" />
              <span className="text-xl md:text-2xl font-bold text-slate-800">
                {stats?.averageRating?.toFixed(1) || '0'}
              </span>
            </div>
            <p className="text-slate-600 text-sm md:text-base">Avg. Rating</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">Filters:</span>
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none
                       focus:ring-2 focus:ring-cyclara-500"
            >
              <option value="">All Statuses</option>
              {Object.entries(statusConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none
                       focus:ring-2 focus:ring-cyclara-500"
            >
              <option value="">All Types</option>
              {Object.entries(typeConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>

            {(filterStatus || filterType) && (
              <button
                onClick={() => { setFilterStatus(''); setFilterType(''); }}
                className="text-sm text-cyclara-500 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feedback List */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-800">Feedback ({feedback.length})</h2>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-500">Loading feedback...</p>
              </div>
            ) : feedback.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No feedback found</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {feedback.map((item) => {
                  const TypeIcon = typeConfig[item.type]?.icon || MessageSquare;
                  return (
                    <div
                      key={item.id}
                      onClick={() => setSelectedFeedback(item)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedFeedback?.id === item.id ? 'bg-cyclara-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          typeConfig[item.type]?.color || 'bg-slate-100'
                        }`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-slate-800">{item.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              statusConfig[item.status]?.color || 'bg-slate-100'
                            }`}>
                              {statusConfig[item.status]?.label || item.status}
                            </span>
                          </div>

                          <p className="text-sm text-slate-600 truncate">{item.message}</p>

                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                            <span>{formatDate(item.created_at)}</span>
                            {item.rating && (
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                {item.rating}/5
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {selectedFeedback ? (
              <>
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <h2 className="font-semibold text-slate-800">Details</h2>
                  <button
                    onClick={() => deleteFeedback(selectedFeedback.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* User Info */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-cyclara-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-cyclara-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{selectedFeedback.name}</p>
                        <a
                          href={`mailto:${selectedFeedback.email}`}
                          className="text-sm text-cyclara-500 hover:underline flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" />
                          {selectedFeedback.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Type & Rating */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      typeConfig[selectedFeedback.type]?.color
                    }`}>
                      {typeConfig[selectedFeedback.type]?.label}
                    </span>

                    {selectedFeedback.rating && (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < selectedFeedback.rating!
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-slate-200'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">Message</p>
                    <p className="text-slate-700 whitespace-pre-wrap">{selectedFeedback.message}</p>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(selectedFeedback.created_at)}</span>
                  </div>

                  {/* Status Update */}
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <button
                          key={key}
                          onClick={() => updateFeedbackStatus(selectedFeedback.id, key)}
                          className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            selectedFeedback.status === key
                              ? config.color + ' font-medium'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {config.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a feedback to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
